/**
 * 物理重构侧栏历史：仅展示 01D–04D，过滤 Job 聚合行与「结果」类 fallback。
 * 不展开 legacy payload.urls[]（风险最低的 P0 策略）。
 */

export const PHYSICAL_RECONSTRUCT_HISTORY_LABELS = ['01D', '02D', '03D', '04D'] as const;
export type PhysicalReconstructHistoryLabel = (typeof PHYSICAL_RECONSTRUCT_HISTORY_LABELS)[number];

const LABEL_SET = new Set<string>(PHYSICAL_RECONSTRUCT_HISTORY_LABELS);

const RESULT_TITLE_BLOCKLIST = new Set(['result', '结果', '结果图']);

/** Job 聚合行 client_item_id：physical_reconstruct-{digits}，无 -0..3 分片后缀 */
const AGGREGATE_CLIENT_ITEM_ID = /^physical_reconstruct-\d+$/;

export function isAggregatePhysicalReconstructHistoryId(id: string): boolean {
  const s = id.trim();
  if (!s) return false;
  if (/-[0-3]$/.test(s)) return false;
  return AGGREGATE_CLIENT_ITEM_ID.test(s);
}

/** 一次任务（同一 base id）内分片后缀 -0..3 的 base 部分 */
export function physicalReconstructHistoryBaseId(id: string): string {
  const s = id.trim();
  const m = s.match(/^(.+)-([0-3])$/);
  return m ? m[1] : s;
}

/**
 * 将 modeLabel 规范为 01D–04D；无法映射（含 Result/结果）则返回 undefined。
 */
export function normalizePhysicalReconstructModeLabel(raw?: string): PhysicalReconstructHistoryLabel | undefined {
  if (raw == null || raw === '') return undefined;
  const s = String(raw).trim();
  if (!s) return undefined;
  const lower = s.toLowerCase();
  if (RESULT_TITLE_BLOCKLIST.has(lower) || RESULT_TITLE_BLOCKLIST.has(s)) return undefined;
  if (/^0[1-4]d$/i.test(s)) {
    const up = s.toUpperCase() as PhysicalReconstructHistoryLabel;
    return LABEL_SET.has(up) ? up : undefined;
  }
  if (s.includes('黄昏') || /DUSK/i.test(s)) return '01D';
  if (s.includes('白天') || /DAY/i.test(s)) return '02D';
  if (s.includes('夜晚') || /NIGHT/i.test(s)) return '03D';
  if (s.includes('阴天') || /CLOUDY/i.test(s)) return '04D';
  return undefined;
}

export function isPhysicalReconstructDisplayHistoryItem(item: {
  id?: string;
  modeLabel?: string;
}): boolean {
  const id = String(item.id ?? '').trim();
  if (id && isAggregatePhysicalReconstructHistoryId(id)) return false;
  const label = normalizePhysicalReconstructModeLabel(item.modeLabel);
  if (!label) return false;
  return true;
}

/**
 * 云端/本地历史进入 UI 前：白名单 + 按任务去重（每任务每标签最多 1 条，最多 4 条）。
 */
export function filterPhysicalReconstructHistoryForDisplay<
  T extends { id: string; modeLabel?: string; timestamp: number },
>(items: T[]): T[] {
  const byBase = new Map<string, Map<PhysicalReconstructHistoryLabel, T>>();

  for (const raw of items) {
    if (!isPhysicalReconstructDisplayHistoryItem(raw)) continue;
    const modeLabel = normalizePhysicalReconstructModeLabel(raw.modeLabel)!;
    const base = physicalReconstructHistoryBaseId(raw.id);
    if (!byBase.has(base)) byBase.set(base, new Map());
    const bucket = byBase.get(base)!;
    const stamped = { ...raw, modeLabel };
    const existing = bucket.get(modeLabel);
    if (!existing || stamped.timestamp > existing.timestamp) {
      bucket.set(modeLabel, stamped);
    }
  }

  const out: T[] = [];
  for (const bucket of byBase.values()) {
    for (const label of PHYSICAL_RECONSTRUCT_HISTORY_LABELS) {
      const it = bucket.get(label);
      if (it) out.push(it);
    }
  }

  return out.sort((a, b) => b.timestamp - a.timestamp);
}
